import Head from 'next/head';
import type { GetStaticProps } from 'next';

import { FeedbackForm } from '@components/whats-new/FeedbackForm';
import { UpdateCard } from '@components/whats-new/UpdateCard';
import {
  SITE_HEADING_CLASS_NAME,
  TEXT_LINK_CLASS_NAME
} from '@components/whats-new/styles';
import { MissingPropsRecovery } from '@components/missing-props-recovery';
import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import { WHATS_NEW_PATH, getProductUpdates } from '@lib/sanity/productUpdate';
import type { SanityProductUpdateSummary } from '@lib/sanity/types';
import {
  FEEDBACK_SECTION_ID,
  WHATS_NEW_FEEDBACK_FORM_ID
} from '@lib/whats-new/feedback';
import { splitFeaturedUpdates } from '@lib/whats-new/model';

// The Sanity webhook regenerates the page on publish; this is the backstop.
export const REVALIDATE_SECONDS = 600;

const PAGE_TITLE = `What's new on the Hub | ${SITE_NAME}`;
const PAGE_DESCRIPTION =
  "What we've just released on the website and in the app, what's coming next, and where we need testers.";
const CANONICAL_URL = `${FFH_HOSTNAME}${WHATS_NEW_PATH}`;
const FEEDBACK_HIDDEN_FIELDS = { feature: 'whats-new', page: WHATS_NEW_PATH };

const PROPS_RELOAD_MARKER_KEY = 'ffh-whats-new-props-reload';
const MISSING_PROPS_RUM_CONTEXT = 'whats-new-missing-props';
const FALLBACK_PATH = '/';

export type WhatsNewPageProps = {
  /** See ArticlePage: a failed client-side data fetch can arrive without it. */
  updates?: SanityProductUpdateSummary[];
};

const WhatsNewPage = ({ updates }: WhatsNewPageProps) => {
  if (!updates) {
    return (
      <MissingPropsRecovery
        context={MISSING_PROPS_RUM_CONTEXT}
        reloadMarkerKey={PROPS_RELOAD_MARKER_KEY}
        fallbackPath={FALLBACK_PATH}
        errorMessage="What's new page props missing"
      />
    );
  }

  const { featured, earlier } = splitFeaturedUpdates(updates);
  const hasFeedbackForm = Boolean(WHATS_NEW_FEEDBACK_FORM_ID);

  return (
    <>
      <Head>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={CANONICAL_URL} key="canonical" />
        <meta property="og:title" content={PAGE_TITLE} key="og:title" />
        <meta property="og:url" content={CANONICAL_URL} key="og:url" />
        <meta
          property="og:description"
          content={PAGE_DESCRIPTION}
          key="og:description"
        />
        <meta property="og:type" content="website" key="og:type" />
      </Head>
      <div className="bg-white text-landing-ink">
        <div className="mx-auto flex max-w-[1120px] flex-col gap-12 px-4 pb-24 pt-10 sm:px-8 sm:pt-16">
          <header className="flex max-w-[760px] flex-col gap-4">
            <h1
              className={`${SITE_HEADING_CLASS_NAME} m-0 text-[2.25rem] leading-[1.08] text-landing-ink [text-wrap:balance] sm:text-[52px]`}
            >
              What&apos;s new on the Hub
            </h1>
            <p className="m-0 max-w-[620px] font-body text-base leading-[1.55] text-[#33475a] sm:text-[19px]">
              {PAGE_DESCRIPTION}
            </p>
            {hasFeedbackForm && (
              <a
                href={`#${FEEDBACK_SECTION_ID}`}
                className={`${TEXT_LINK_CLASS_NAME} self-start`}
              >
                Tell us what to build next
              </a>
            )}
          </header>

          {featured.length > 0 ? (
            <section
              aria-label="Latest updates"
              className="flex flex-col gap-6"
            >
              {featured.map((update) => (
                <UpdateCard
                  key={update._id}
                  update={update}
                  variant="featured"
                />
              ))}
            </section>
          ) : (
            <p
              className="m-0 max-w-[600px] rounded-2xl bg-landing-surface px-6 py-5 font-body text-base leading-[1.6] text-[#33475a]"
              data-cy="whats-new-empty"
            >
              Nothing here yet. New features, improvements and calls for beta
              testers will appear here as we ship them.
            </p>
          )}

          {earlier.length > 0 && (
            <section
              aria-labelledby="earlier-updates"
              className="flex flex-col gap-5"
            >
              <h2
                id="earlier-updates"
                className="m-0 font-heading text-xl font-extrabold text-landing-ink"
              >
                Earlier updates
              </h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {earlier.map((update) => (
                  <UpdateCard
                    key={update._id}
                    update={update}
                    variant="compact"
                  />
                ))}
              </div>
            </section>
          )}

          {hasFeedbackForm && (
            <section
              id={FEEDBACK_SECTION_ID}
              aria-labelledby="feedback-heading"
              className="grid scroll-mt-24 gap-8 rounded-2xl bg-landing-surface p-6 sm:p-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
            >
              <div className="flex flex-col gap-3">
                <h2
                  id="feedback-heading"
                  className={`${SITE_HEADING_CLASS_NAME} m-0 text-[28px] leading-[1.15] text-landing-ink`}
                >
                  Tell us what to build next
                </h2>
                <p className="m-0 font-body text-base leading-[1.6] text-[#33475a]">
                  We read everything that comes through here. We can&apos;t
                  build every idea straight away, but when enough of you ask for
                  something it goes on the roadmap, and you&apos;ll see it show
                  up on this page.
                </p>
              </div>
              <FeedbackForm hiddenFields={FEEDBACK_HIDDEN_FIELDS} />
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps<WhatsNewPageProps> = async () => {
  return {
    props: { updates: await getProductUpdates() },
    revalidate: REVALIDATE_SECONDS
  };
};

export default WhatsNewPage;
